import { cloneDeep, get, set } from "lodash";
import { useContext } from "react";
import { BaseCheckbox } from "./BaseCheckbox";
import { BaseInput } from "./BaseInput";
import { BaseSelect } from "./BaseSelect";
import { ObjectEditorContext } from "./ObjectEditorContext";

interface ItemInputProps extends React.HTMLAttributes<HTMLInputElement | HTMLSelectElement> {
  dataPoint: string;
}

/**
 * Input wrapper for a single templated item within an object editor.
 * Chooses the appropriate input type (checkbox, select, text) based on
 * the template and wires value changes back to the parent object.
 *
 * @param props - Component props including `dataPoint` which is the dotted
 *        path to the property being edited.
 * @param props.dataPoint - The dotted property path within the object.
 */
export const ItemInput = ({ dataPoint, ...rest }: ItemInputProps) => {
  const objectEditorContext = useContext(ObjectEditorContext);
  const thisOption = dataPoint.split(".").pop();
  const thisOptionType = objectEditorContext?.objectTemplate.find(
    (item) => item.name === thisOption,
  )?.type;
  const thisOptionValues = objectEditorContext?.objectTemplate.find(
    (item) => item.name === thisOption,
  )?.options;
  const thisOptionLabel =
    objectEditorContext?.objectTemplate.find((item) => item.name === thisOption)?.label ??
    thisOption;
  const thisValueRaw = get(objectEditorContext?.object, dataPoint) as
    | string
    | number
    | boolean
    | undefined;

  /**
   * Update the parent object with the new value for this data point.
   *
   * @param ret - The new value (string|number|boolean|undefined) provided
   *        by the child input component.
   */
  const handleChange = (ret: string | number | boolean | undefined) => {
    /* istanbul ignore else */
    if (objectEditorContext) {
      const newObject = cloneDeep(objectEditorContext.object);
      /* istanbul ignore else */
      if (get(newObject, dataPoint) !== ret) {
        set(newObject, dataPoint, ret);
        objectEditorContext.setObject(newObject);
      }
    }
  };

  return !objectEditorContext ? null : (
    <>
      {thisOptionType === "boolean" ? (
        <BaseCheckbox
          id={`id-${dataPoint}`}
          checked={(thisValueRaw ?? false) as boolean}
          change={handleChange}
          label={thisOptionLabel}
          {...rest}
        />
      ) : thisOptionType === "select" && thisOptionValues ? (
        <BaseSelect
          id={`id-${dataPoint}`}
          value={thisValueRaw as string | number | undefined}
          availableOptions={thisOptionValues}
          change={handleChange}
          label={thisOptionLabel}
          {...rest}
        />
      ) : (
        <BaseInput
          id={`id-${dataPoint}`}
          value={thisValueRaw as string | number | undefined}
          onNewValue={handleChange}
          label={thisOptionLabel}
          {...rest}
        />
      )}
    </>
  );
};

ItemInput.displayName = "ItemInput";
